// The small, consistent vocabulary every screen is built from.
//
// Read only. A login that cannot write (session.canWrite is false: an AI employee today) never sees a
// control it cannot use. The kit does it in one place: a Btn is not drawn unless it only reads (`read`),
// an Edit is drawn as its value, a Field draws the control inside it as its value, and the frame's
// `ro` class does the same for any raw input, select or button a screen still holds (styles.css,
// "Read only"). A control that only reads says so with `read` (a Btn prop, or the class on a raw one)
// and is left alone. Whatever still fires a write is stopped in lib/api.ts with one message.

import { Children, isValidElement, useEffect, useId, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Trace } from '@zerogap/engine';
import { money } from '../lib/format';
import { useSession } from '../lib/session';

export function Panel({ label, right, soft, children, className }: { label?: ReactNode; right?: ReactNode; soft?: boolean; children?: ReactNode; className?: string }) {
  return (
    <div className={`panel${soft ? ' soft' : ''}${className ? ` ${className}` : ''}`}>
      {(label || right) && (
        <div className="lb">
          <span>{label}</span>
          {right && <span>{right}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * A panel that folds: tap its heading to open or close it. `share` draws the section's part of the whole
 * as a bar in the heading (the largest section's bar fills the track), so a list of folded sections reads as a bar chart (Spencer, 2026-10-17: "quickly
 * visually see where the majority of the labor is falling").
 */
export function Fold({ open, onToggle, label, right, share, children, className }: { open: boolean; onToggle: () => void; label: ReactNode; right?: ReactNode; /** This section's part of the whole, 0 to 1, and the largest part among its sections. */ share?: { part: number; most: number }; children?: ReactNode; className?: string }) {
  return (
    <div className={`panel card${open ? '' : ' folded'}${share !== undefined ? ' shared' : ''}${className ? ` ${className}` : ''}`}>
      <div className="lb">
        <span className="fold" {...pressable(onToggle)} aria-expanded={open}>
          {open ? '▾' : '▸'} {label}
        </span>
        {share !== undefined && <Share part={share.part} most={share.most} />}
        {right && <span className="right">{right}</span>}
      </div>
      {open && children}
    </div>
  );
}

/** A part of a whole as its percent, and a bar as long against the track as the part is against the largest of its kind (`most`). */
export function Share({ part, most = 1 }: { part: number; most?: number }) {
  const pct = Math.max(0, Math.min(1, part)) * 100;
  const fill = most > 0 ? Math.max(0, Math.min(1, part / most)) * 100 : 0;
  // a sliver under half a percent still shows, and still says so
  const words = pct > 0 && pct < 0.5 ? '<1%' : `${Math.round(pct)}%`;
  return (
    <span className="share" title={`${pct.toFixed(1)}% of the whole`}>
      <span className="bar">
        <i style={{ width: `${fill}%` }} />
      </span>
      <b>{words}</b>
    </span>
  );
}

/**
 * Which folds on a screen are open, kept for the browser session under `scope` (a job's tab, a catalog item),
 * so leaving a tab and coming back finds them as they were. `openByDefault` is how a fold starts.
 */
export function useFolds(scope: string, openByDefault = false) {
  const read = (): Record<string, boolean> => {
    try {
      return JSON.parse(sessionStorage.getItem(`zg.fold.${scope}`) ?? '{}') as Record<string, boolean>;
    } catch {
      return {};
    }
  };
  const [state, setState] = useState<Record<string, boolean>>(read);
  useEffect(() => setState(read()), [scope]); // eslint-disable-line react-hooks/exhaustive-deps
  // merged into what is stored, so two screens keeping folds under one scope never undo each other
  const put = (change: Record<string, boolean>) => {
    const next = { ...read(), ...change };
    setState(next);
    try {
      sessionStorage.setItem(`zg.fold.${scope}`, JSON.stringify(next));
    } catch {
      /* no session storage: the folds simply start as they do */
    }
  };
  const isOpen = (k: string) => state[k] ?? openByDefault;
  return {
    isOpen,
    toggle: (k: string) => put({ [k]: !isOpen(k) }),
    /** Opens or folds every one of `keys`. */
    setAll: (keys: string[], open: boolean) => put(Object.fromEntries(keys.map((k) => [k, open]))),
  };
}

/** True when a click landed on a link inside a clickable row: the link navigates on its own (and a Cmd-click, a middle click or a right-click copy works), so the row must not act too. */
export function onLink(e: { target: EventTarget | null }): boolean {
  return !!(e.target as HTMLElement | null)?.closest?.('a[href]');
}

/** What a press handler may read: the same from a click and from Enter or Space (the modifier keys say "add to the selection"). */
export type Press = Pick<React.MouseEvent | React.KeyboardEvent, 'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey' | 'target' | 'currentTarget' | 'stopPropagation' | 'preventDefault'>;

/**
 * The props that put a clickable element within reach of the keyboard: a tab stop that Enter and Space press
 * (design review D10, 2026-10-14). A key pressed on a button, a link or a field inside the element is that
 * control's own and is left to it. `row` keeps a table row's own role (a row that reads as a button is no longer a row).
 */
export function pressable(onPress: (e: Press) => void, row?: 'row') {
  return {
    role: row ? undefined : ('button' as const),
    tabIndex: 0,
    onClick: (e: React.MouseEvent) => onPress(e),
    onKeyDown: (e: React.KeyboardEvent) => {
      if ((e.key !== 'Enter' && e.key !== ' ') || e.target !== e.currentTarget) return;
      e.preventDefault();
      onPress(e);
    },
  };
}

export function Ln({ left, right, under, onClick, sel, sub, className }: { left: ReactNode; right?: ReactNode; /** A short line under the row, full width: what a type choice stands for on this job. */ under?: ReactNode; onClick?: (e: Press) => void; sel?: boolean; sub?: boolean; className?: string }) {
  const cls = `ln${under ? ' under' : ''}${onClick ? ' click' : ''}${sel ? ' sel' : ''}${sub ? ' sub' : ''}${className ? ` ${className}` : ''}`;
  const press = onClick ? pressable((e) => !onLink(e) && onClick(e)) : {};
  if (!under) {
    return (
      <div className={cls} {...press}>
        <span>{left}</span>
        {right !== undefined && <span className="right">{right}</span>}
      </div>
    );
  }
  return (
    <div className={cls} {...press}>
      <div className="ln-row">
        <span>{left}</span>
        {right !== undefined && <span className="right">{right}</span>}
      </div>
      <div className="ln-under">{under}</div>
    </div>
  );
}

export function Tag({ children, kind, title }: { children: ReactNode; kind?: 'un' | 'on' | 'off' | 'check'; title?: string }) {
  return (
    <span className={`tag${kind ? ` ${kind}` : ''}`} title={title}>
      {children}
    </span>
  );
}

/** The raw controls that would change a row: what the `ro` frame draws as a value and keeps from taking a key. A search box, a file picker and anything marked `read` are not among them. */
export const WRITE_CONTROLS = 'input:not([type=search]):not([type=file]):not(.read), select:not(.read), textarea:not(.read)';

export function Unverified({ what = 'unverified' }: { what?: string }) {
  return <Tag kind="un">{what}</Tag>;
}

/** A button. One that changes nothing (Close, Print, Copy, a view switch) says `read` and is drawn for everyone; the rest are drawn only for a login that may write. */
export function Btn({ children, onClick, primary, small, big, disabled, title, type, read }: { children: ReactNode; onClick?: (e: React.MouseEvent) => void; primary?: boolean; small?: boolean; big?: boolean; disabled?: boolean; title?: string; type?: 'button' | 'submit'; read?: boolean }) {
  const { canWrite } = useSession();
  if (!canWrite && !read) return null;
  return (
    <button type={type ?? 'button'} className={`btn${primary ? ' p' : ''}${small ? ' sm' : ''}${big ? ' big' : ''}${read ? ' read' : ''}`} onClick={onClick} disabled={disabled} title={title}>
      {children}
    </button>
  );
}

/**
 * A popover drawn at the top of the page, under its button (aligned to the button's right with `left`), never
 * inside the button: a table that scrolls cannot clip it, its text can be selected and copied, and Escape, a tap
 * outside, a scroll or a resize closes it. The Info dot and the account panel in the top bar share it.
 */
export function usePopover(left?: boolean) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState({ top: 0, left: 0 });
  const id = useId();
  // under the button, kept inside the window; above it when there is no room below
  useLayoutEffect(() => {
    if (!open || !btn.current || !pop.current) return;
    const b = btn.current.getBoundingClientRect();
    const p = pop.current.getBoundingClientRect();
    const edge = 8;
    const x = Math.max(edge, Math.min(left ? b.right + edge - p.width : b.left - edge, window.innerWidth - edge - p.width));
    const below = b.bottom + 4;
    const y = below + p.height > window.innerHeight - edge ? Math.max(edge, b.top - 4 - p.height) : below;
    setAt({ top: y, left: x });
  }, [open, left]);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const outside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btn.current?.contains(t) && !pop.current?.contains(t)) close();
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', key);
    // the popover is placed against the window, so it closes rather than drift when the page under it moves
    document.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', key);
      document.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);
  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen((o) => !o);
  };
  return { open, toggle, close: () => setOpen(false), btn, pop, at, id };
}

/** Explanation on demand: nothing explains itself until the dot is tapped (design review D3, 2026-10-14). */
export function Info({ children, left }: { children: ReactNode; left?: boolean }) {
  const { open, toggle, btn, pop, at, id } = usePopover(left);
  return (
    <>
      <button type="button" ref={btn} className="info" aria-label="Explain" aria-expanded={open} aria-describedby={open ? id : undefined} onClick={toggle}>
        i
      </button>
      {open &&
        createPortal(
          <div ref={pop} id={id} role="tooltip" className="pop" style={{ top: at.top, left: at.left }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}

/** The chain behind a number: the formula, and the values it saw. */
export function Chain({ expr, trace, note, left }: { expr?: string | null; trace?: Trace[]; note?: ReactNode; left?: boolean }) {
  return (
    <Info left={left}>
      {note && <div style={{ marginBottom: 6 }}>{note}</div>}
      {expr && (
        <div>
          <code>{expr}</code>
        </div>
      )}
      {trace && trace.length > 0 && (
        <table style={{ marginTop: 6 }}>
          <tbody>
            {trace.map((t) => (
              <tr key={t.name}>
                <td style={{ padding: '2px 8px 2px 0', border: 0 }}>{t.name}</td>
                <td style={{ padding: '2px 0', border: 0 }} className="num right">
                  {t.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Info>
  );
}

/** A dollar figure. People without the "see prices" permission never see one. */
export function Money({ cents, whole, sign }: { cents: number | null | undefined; whole?: boolean; sign?: boolean }) {
  const { can } = useSession();
  if (!can('see_prices')) return <span className="muted">—</span>;
  return <span className="num">{money(cents, { whole, sign })}</span>;
}

/** The tab strip. `sticky` keeps it at the top of the scrolling pane once the page has scrolled past it. */
export function Tabs<T extends string>({ items, value, onChange, sticky }: { items: { key: T; label: ReactNode }[]; value: T; onChange: (k: T) => void; sticky?: boolean }) {
  return (
    <div className={`tabs${sticky ? ' stick' : ''}`} role="tablist">
      {items.map((i) => (
        <button key={i.key} role="tab" aria-selected={i.key === value} className={i.key === value ? 'on' : ''} onClick={() => onChange(i.key)}>
          {i.label}
        </button>
      ))}
    </div>
  );
}

export function Seg<T extends string>({ items, value, onChange }: { items: { key: T; label: ReactNode }[]; value: T; onChange: (k: T) => void }) {
  return (
    <span className="seg">
      {items.map((i) => (
        <button key={i.key} type="button" className={`tag${i.key === value ? ' on' : ''}`} onClick={() => onChange(i.key)}>
          {i.label}
        </button>
      ))}
    </span>
  );
}

export function Head({ title, sub, children }: { title: ReactNode; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className="head">
      <div>
        <h1>{title}</h1>
        {sub && <small>{sub}</small>}
      </div>
      {children && <div className="actions">{children}</div>}
    </div>
  );
}

/**
 * A dialog. Escape and the Close button always close it; a click on the backdrop closes it only while no field inside
 * holds text, so a slip of the mouse never discards what was typed. Focus moves to the first field (else to Close) when
 * it opens, unless a field of its own took it already (autoFocus), and goes back to whatever opened it when it closes;
 * the title labels the dialog (design review D9, 2026-10-14).
 */
export function Modal({ title, onClose, children, wide }: { title: ReactNode; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // what opened the dialog, read on the first render: once the dialog is in the page a field of its own may hold focus
  const opener = useRef<HTMLElement | null>(null);
  if (opener.current === null) opener.current = document.activeElement as HTMLElement | null;
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);
  useEffect(() => {
    const el = box.current;
    if (el && !el.contains(document.activeElement)) {
      const first = el.querySelector<HTMLElement>('input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled])') ?? el.querySelector<HTMLElement>('.head button');
      (first ?? el).focus();
    }
    return () => {
      const back = opener.current;
      if (back?.isConnected && back !== document.body) back.focus();
    };
  }, []);
  // what was typed: a text-like field inside the dialog that is not empty
  const typed = () =>
    [...(box.current?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea') ?? [])].some(
      (f) => !['checkbox', 'radio', 'hidden', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(f.type) && f.value.trim() !== '',
    );
  return (
    <div className="modal-bg" onMouseDown={(e) => e.target === e.currentTarget && !typed() && onClose()}>
      <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={box} tabIndex={-1}>
        <div className="head">
          <h2 id={titleId}>{title}</h2>
          <Btn small read onClick={onClose}>
            Close
          </Btn>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Loading({ what = 'Loading' }: { what?: string }) {
  return <div className="empty">{what}…</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

/** The text an option list shows for a value: the matching option's words, else the value itself. */
function optionWords(options: ReactNode, value: unknown): ReactNode {
  let found: ReactNode | undefined;
  const walk = (node: ReactNode) => {
    Children.forEach(node, (c) => {
      if (found !== undefined || !isValidElement(c)) return;
      const p = c.props as { value?: unknown; children?: ReactNode };
      if (c.type === 'option') {
        const v = p.value === undefined ? p.children : p.value;
        if (String(v ?? '') === String(value ?? '')) found = p.children;
      } else if (p.children) walk(p.children);
    });
  };
  walk(options);
  return found ?? (value === null || value === undefined || value === '' ? undefined : String(value));
}

/** A control drawn as its value, for a login that cannot write: a text box's text, a list's chosen words, a tick's yes or no. */
export function Value({ children, muted, className, style }: { children?: ReactNode; muted?: boolean; className?: string; style?: React.CSSProperties }) {
  const empty = children === undefined || children === null || children === '' || children === false;
  return (
    <span className={`val${empty || muted ? ' muted' : ''}${className ? ` ${className}` : ''}`} style={style}>
      {empty ? '—' : children}
    </span>
  );
}

/** What a form control holds, as words, for a login that cannot write; undefined leaves the element as it is (a search box, a file picker, anything marked `read`). */
export function asValue(el: ReactElement): ReactNode | undefined {
  const p = el.props as Record<string, any>;
  if (typeof el.type !== 'string' || !['input', 'select', 'textarea'].includes(el.type)) return undefined;
  if (String(p.className ?? '').split(' ').includes('read')) return undefined;
  const type = el.type === 'input' ? String(p.type ?? 'text') : el.type;
  if (type === 'search' || type === 'file' || type === 'hidden' || type === 'button' || type === 'submit') return undefined;
  if (type === 'checkbox' || type === 'radio') return <Value>{p.checked ? 'Yes' : 'No'}</Value>;
  if (el.type === 'select') return <Value>{optionWords(p.children, p.value ?? p.defaultValue)}</Value>;
  const v = p.value ?? p.defaultValue;
  return <Value>{v === undefined || v === null || v === '' ? undefined : String(v)}</Value>;
}

/** A labelled control. For a login that cannot write, the control inside is drawn as its value. */
export function Field({ label, children, wide, read }: { label: ReactNode; children: ReactNode; wide?: boolean; /** Drawn as a control for every login, like a Btn's `read`: the sign-in screen, where no one is signed in yet and nothing is written. */ read?: boolean }) {
  const { canWrite } = useSession();
  const inside = canWrite || read
    ? children
    : Children.map(children, (c) => {
        if (!isValidElement(c)) return c;
        const v = asValue(c);
        return v === undefined ? c : v;
      });
  return (
    <label className={`field${wide ? ' wide' : ''}`}>
      <span>{label}</span>
      {inside}
    </label>
  );
}

/**
 * A text box that saves when you leave it or press Enter, and puts back the old value on
 * Escape. `parse` may refuse the text (return undefined) to keep a bad value out.
 */
export function Edit({
  value, onSave, parse, placeholder, className, type, disabled, list, style, multiline,
}: {
  value: string | number | null | undefined;
  onSave: (v: string) => void | Promise<unknown>;
  parse?: (text: string) => string | undefined;
  placeholder?: string;
  className?: string;
  type?: string;
  disabled?: boolean;
  list?: string;
  style?: React.CSSProperties;
  /** A box of several lines that grows with its text (an item's notes): Enter starts a new line, Ctrl+Enter or leaving the box saves. */
  multiline?: boolean;
}) {
  const { canWrite } = useSession();
  const shown = value === null || value === undefined ? '' : String(value);
  const [text, setText] = useState(shown);
  const [bad, setBad] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    setText(shown);
    setBad(false);
  }, [shown]);
  // the box is as tall as its text, never shorter than two lines, so nothing is cut off (Spencer, 2026-10-16)
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [text, multiline]);
  // a login that cannot write sees the value as words, never a box
  if (!canWrite) return <Value className={className} style={multiline ? { whiteSpace: 'pre-wrap', ...style } : style}>{shown}</Value>;
  const commit = () => {
    if (text === shown) return;
    const out = parse ? parse(text) : text;
    if (out === undefined) {
      setBad(true);
      return;
    }
    setBad(false);
    void onSave(out);
  };
  if (multiline)
    return (
      <textarea
        ref={area}
        className={className}
        style={bad ? { ...style, outline: '2px solid var(--bad)' } : style}
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        rows={2}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) (e.target as HTMLTextAreaElement).blur();
          if (e.key === 'Escape') {
            setText(shown);
            setBad(false);
          }
        }}
      />
    );
  return (
    <input
      type={type ?? 'text'}
      className={className}
      style={bad ? { ...style, outline: '2px solid var(--bad)' } : style}
      value={text}
      placeholder={placeholder}
      disabled={disabled}
      list={list}
      title={bad ? 'Not understood. Try 24 3/8 or 24.375.' : text.length > 24 ? text : undefined}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setText(shown);
          setBad(false);
        }
      }}
    />
  );
}
